import type { TypedPropertyFilterQuery } from '@risksmart-app/components/src/table/tableUtils';
import { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { Ref } from 'react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { EnterpriseRiskRegisterFields } from 'src/pages/enterprise-risk/types';
import { useRiskRatingResolver } from 'src/ratings/useRiskRatingResolver';
import { merge } from 'ts-deepmerge';

import { emptyFilterQuery } from '@/utils/table/types';

import { useDashboardWidgetSettings } from '../../../../context/useDashboardWidgetSettings';
import { useGetWidgetData } from '../../gigawidget/hooks/useGetWidgetData';
import type { WidgetRef } from '../../types';
import { dataSources } from '../../universal-widget/data-sources';
import {
  convertToTokenGroups,
  dashboardFilterToQuery,
} from '../../universal-widget/util';
import { useDashboardFilters } from '../../useDashboardStore';
import { useWidgetSettingsHandler } from '../../widget-context/useWidgetSettingsHandler';
import HeatmapWidget from '../heatmap-widget/HeatmapWidget';
import { EnterpriseRiskHeatmapSettingsModal } from './EnterpriseRiskHeatmapSettingsModal';
import type { EnterpriseRiskHeatmapSettings } from './enterpriseRiskHeatmapSettingsSchema';
import { useGetEnterpriseRiskRatingsData } from './useGetEnterpriseRiskRatingsData';

type Props = {
  controlType: Risk_Assessment_Result_Control_Type_Enum;
};

/**
 * Plots enterprise risks on the risk-assessment likelihood × impact matrix
 * (RSP-6195), positioned by their aggregated dimensions. Which aggregate —
 * mean, median, or worst case — is a widget setting (default mean).
 *
 * Deliberately separate from the risk heatmap widget (no generalisation, per
 * product direction) — only the matrix renderer (HeatmapWidget) is shared.
 */
export const EnterpriseRiskHeatmap = forwardRef(
  (props: Props, ref: Ref<WidgetRef>) => {
    const [showModal, setShowModal] = useState(false);
    const [settings, setSettings] =
      useDashboardWidgetSettings<EnterpriseRiskHeatmapSettings>();
    const navigate = useNavigate();

    const aggregation = settings?.aggregation ?? 'mean';
    const residual =
      props.controlType === Risk_Assessment_Result_Control_Type_Enum.Controlled;

    const {
      options: { likelihood: likelihoodOptions, impact: impactOptions },
    } = useRiskRatingResolver('standard', { configType: 'risk_assessment' });

    const impactIndexMap = useMemo(
      () => new Map(impactOptions.map((o, i) => [o.value, i])),
      [impactOptions]
    );
    const likelihoodIndexMap = useMemo(
      () => new Map(likelihoodOptions.map((o, i) => [o.value, i])),
      [likelihoodOptions]
    );
    const getImpactIndex = useCallback(
      (v: number) => impactIndexMap.get(v),
      [impactIndexMap]
    );
    const getLikelihoodIndex = useCallback(
      (v: number) => likelihoodIndexMap.get(v),
      [likelihoodIndexMap]
    );
    const getImpactLabel = useCallback(
      (index: number) => impactOptions[index]?.label ?? '',
      [impactOptions]
    );
    const getLikelihoodLabel = useCallback(
      (index: number) => likelihoodOptions[index]?.label ?? '',
      [likelihoodOptions]
    );

    useWidgetSettingsHandler(ref, () => setShowModal(true));

    const filters = useDashboardFilters();
    const dataSource = dataSources.enterpriseRisk;
    const {
      tableProps: { allItems },
      loading,
    } = useGetWidgetData({
      dataSource,
      propertyFilterQuery: settings?.filtering,
    });
    const handleSave = async (data: EnterpriseRiskHeatmapSettings) => {
      setSettings(data);
    };

    const data = useGetEnterpriseRiskRatingsData({
      controlType: props.controlType,
      aggregation,
      enterpriseRisks: allItems,
      getImpactIndex,
      getLikelihoodIndex,
    });

    const aggregationSuffix =
      aggregation === 'worstCase'
        ? 'WorstCase'
        : aggregation === 'median'
          ? 'Median'
          : 'Mean';

    return (
      <>
        {showModal && (
          <EnterpriseRiskHeatmapSettingsModal
            onDismiss={() => setShowModal(false)}
            onSave={handleSave}
            dataSource={dataSource}
          />
        )}
        <HeatmapWidget
          controlType={props.controlType}
          getImpactLabelByIndex={getImpactLabel}
          getLikelihoodLabelByIndex={getLikelihoodLabel}
          onCellClick={(cell) => {
            const localPropertyFilter: TypedPropertyFilterQuery<EnterpriseRiskRegisterFields> =
              settings?.filtering
                ? convertToTokenGroups(settings.filtering)
                : emptyFilterQuery;
            // Filters the register on the labelled dimension columns. Exact
            // for whole values (mean/worst case); a fractional median (e.g.
            // "2.5") is plotted in its nearest cell but labelled with the
            // number, so it won't match the cell's label filter.
            const cellPropertyFilter: TypedPropertyFilterQuery<EnterpriseRiskRegisterFields> =
              {
                operation: 'and',
                tokens: [],
                tokenGroups: [
                  {
                    propertyKey: residual
                      ? `ResidualImpact${aggregationSuffix}Labelled`
                      : `InherentImpact${aggregationSuffix}Labelled`,
                    value: getImpactLabel(cell.x),
                    operator: '=',
                  },
                  {
                    propertyKey: residual
                      ? `ResidualLikelihood${aggregationSuffix}Labelled`
                      : `InherentLikelihood${aggregationSuffix}Labelled`,
                    value: getLikelihoodLabel(cell.y),
                    operator: '=',
                  },
                ],
              };
            const url = dataSource.clickThroughUrl?.(
              // ts-deepmerge's Merge<> mangles the differing token-array
              // element unions into an index-signature object type; the
              // runtime value is a plain filter query of this shape.
              merge(
                localPropertyFilter,
                dashboardFilterToQuery(filters, 'day', undefined, {
                  departments:
                    !!dataSource.dashboardFilterConfig.departmentsFilter,
                  tags: !!dataSource.dashboardFilterConfig.tagsFilter,
                }),
                cellPropertyFilter
              ) as TypedPropertyFilterQuery<EnterpriseRiskRegisterFields>
            );
            if (url) {
              navigate(url);
            }
          }}
          data={data}
          loading={loading}
        />
      </>
    );
  }
);

EnterpriseRiskHeatmap.displayName = 'EnterpriseRiskHeatmap';

export default EnterpriseRiskHeatmap;
